/**
 * Festlegung F72 — eine Zahl aus einer Teilmenge trägt ihren Umfang mit sich.
 *
 * ═══ Worum es geht ════════════════════════════════════════════════════════
 *
 * Die Förderhöhe einer Systemgruppe ist der Druckverlust ihres ungünstigsten
 * Wegs. Gesucht wird er unter den Wegen, die GERECHNET werden konnten — und
 * das sind nicht alle: Verbraucher ohne Anschluss, ohne Systemseite oder
 * hinter einer Netzlücke bekommen eine Zeile ohne Ergebnis.
 *
 * Liegt der wirklich ungünstigste Verbraucher unter den ungerechneten, ist
 * die ausgewiesene Förderhöhe zu klein. Zu groß kann sie nicht sein. Sie ist
 * damit eine UNTERE SCHRANKE, und das ist keine Ungenauigkeit, sondern eine
 * Eigenschaft der Zahl.
 *
 * ═══ Warum die Marke an der Zahl steht und nicht daneben ══════════════════
 *
 * `wegeMitErgebnis` und `wege` standen schon immer in der Antwort — eine
 * Kachel weiter, eine Spalte weiter, im PDF zwei Spalten links. Wer die
 * Förderhöhe las, las eine Zahl ohne Vorbehalt und hatte recht damit, denn
 * nichts an ihr sagte etwas anderes.
 *
 * Deshalb das `≥` unmittelbar vor dem Wert: Es ist nicht wegzulesen, es
 * überlebt das Kopieren in eine Tabelle, und es sagt genau das Richtige —
 * mindestens so viel, möglicherweise mehr.
 *
 * ═══ Was hier NICHT passiert ══════════════════════════════════════════════
 *
 * Nichts wird hochgerechnet, geschätzt oder extrapoliert. Die Lücke bleibt
 * eine Lücke; sie wird nur sichtbar.
 */

import { StatusBadge } from '../../muster';

export type Vollstaendigkeit = 'vollstaendig' | 'untere_schranke' | null;

/** Die Kennzeichnung aus den beiden Zählern — dieselbe Regel wie im Kern. */
export function vollstaendigkeitAus(
  wege: number | null | undefined,
  wegeMitErgebnis: number | null | undefined,
): Vollstaendigkeit {
  const w = wege ?? 0;
  if (w <= 0) return null;
  return (wegeMitErgebnis ?? 0) >= w ? 'vollstaendig' : 'untere_schranke';
}

export const istSchranke = (v: Vollstaendigkeit | undefined) =>
  v === 'untere_schranke';

/**
 * Der Satz, der unter der Zahl steht. Er nennt die Grundgesamtheit, weil
 * „untere Schranke" allein niemandem sagt, wie weit unten.
 */
export function schrankentext(
  wege: number | null | undefined,
  wegeMitErgebnis: number | null | undefined,
): string {
  const w = wege ?? 0;
  const m = wegeMitErgebnis ?? 0;
  const anteil = w > 0 ? Math.round((m / w) * 100) : 0;

  return `UNTERE SCHRANKE: gerechnet aus ${m} von ${w} Wegen (${anteil} %). `
    + `${w - m} Verbraucher dieser Gruppe haben keinen Weg — liegt der `
    + 'ungünstigste unter ihnen, ist die Förderhöhe zu klein. Zu groß kann '
    + 'sie nicht sein. Nicht hochgerechnet.';
}

/**
 * Die Förderhöhe MIT ihrer Kennzeichnung: `≥ 123,45` statt `123,45`.
 *
 * Bewusst ein reiner String und kein Element — er wird auch in
 * Kacheln, `title`-Attributen und Tabellenzellen gebraucht, die kein
 * React-Kind aufnehmen.
 */
export function mitSchranke(
  text: string, v: Vollstaendigkeit | undefined,
): string {
  return istSchranke(v) && text !== '—' ? `≥ ${text}` : text;
}

/**
 * Die Marke neben der Zahl. Sie ist `warnung` und nicht `schlecht`: Die
 * Rechnung ist richtig, ihre Grundlage ist unvollständig — das sind zwei
 * verschiedene Befunde, und „rot" ist für den anderen vergeben.
 */
export function Schrankenmarke({ vollstaendigkeit, wege, wegeMitErgebnis }: {
  vollstaendigkeit: Vollstaendigkeit | undefined;
  wege: number | null | undefined;
  wegeMitErgebnis: number | null | undefined;
}) {
  if (!istSchranke(vollstaendigkeit)) return null;

  // TH-2: StatusBadge statt alter Marke; der lange Satz steht zusätzlich sichtbar
  // dort, wo die Gruppe gezeigt wird (kein Wissen nur im Tooltip, S1.10 Nr. 9).
  return (
    <StatusBadge klein zustand="warnung" text="untere Schranke"
                 titel={schrankentext(wege, wegeMitErgebnis)} />
  );
}
